import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { JwtResponse, LoginRequest, SignupRequest, MessageResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8080/ws';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              console.log('Attempting to refresh token...');
              const response = await this.refreshToken(refreshToken);
              const { token } = response.data;
              
              console.log('Token refreshed successfully');
              localStorage.setItem('accessToken', token);
              originalRequest.headers.Authorization = `Bearer ${token}`;
              
              return this.api(originalRequest);
            } else {
              console.log('No refresh token available, redirecting to login');
              this.clearAuthAndRedirect();
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            this.clearAuthAndRedirect();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(loginRequest: LoginRequest): Promise<AxiosResponse<JwtResponse>> {
    return this.api.post('/auth/signin', loginRequest);
  }

  async signup(signupRequest: SignupRequest): Promise<AxiosResponse<MessageResponse>> {
    return this.api.post('/auth/signup', signupRequest);
  }

  async refreshToken(refreshToken: string): Promise<AxiosResponse<JwtResponse>> {
    return this.api.post('/auth/refresh-token', { refreshToken });
  }

  // Helper method to clear auth data and redirect to login
  private clearAuthAndRedirect() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionTimeout');
    // Use window.location.href for hard redirect to clear any React state
    window.location.href = '/login';
  }

  // Method to retry a failed request after token refresh
  async retryRequest<T>(requestFn: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Try to refresh token and retry once
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const refreshResponse = await this.refreshToken(refreshToken);
            const { token } = refreshResponse.data;
            localStorage.setItem('accessToken', token);
            
            // Retry the original request
            return await requestFn();
          }
        } catch (refreshError) {
          console.error('Token refresh failed during retry:', refreshError);
          this.clearAuthAndRedirect();
        }
      }
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<AxiosResponse<MessageResponse>> {
    return this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<AxiosResponse<MessageResponse>> {
    return this.api.post('/auth/reset-password', { token, newPassword });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<AxiosResponse<MessageResponse>> {
    return this.api.post('/auth/change-password', { currentPassword, newPassword });
  }

  // User endpoints
  async getUserProfile(): Promise<AxiosResponse<any>> {
    return this.retryRequest(() => this.api.get('/users/profile'));
  }

  async updateUserProfile(profileData: any): Promise<AxiosResponse<MessageResponse>> {
    return this.api.put('/users/profile', profileData);
  }

  async getUserById(id: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/users/${id}`);
  }

  // Skills endpoints
  async getAllSkills(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/skills');
  }

  async getSkillById(id: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/skills/${id}`);
  }

  async getSkillsByCategory(categoryId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/skills/category/${categoryId}`);
  }

  async searchSkills(query: string): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/skills/search?query=${encodeURIComponent(query)}`);
  }

  async testDatabase(): Promise<AxiosResponse<any>> {
    return this.api.get('/skills/test-db');
  }

  async createSkill(skillData: {
    name: string;
    description?: string;
    categoryId: number;
  }): Promise<AxiosResponse<any>> {
    return this.api.post('/skills', skillData);
  }

  async updateSkill(id: number, skillData: {
    name: string;
    description?: string;
    categoryId: number;
  }): Promise<AxiosResponse<any>> {
    return this.api.put(`/skills/${id}`, skillData);
  }

  async deleteSkill(id: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/skills/${id}`);
  }

  // Skill categories endpoints
  async getAllSkillCategories(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/skill-categories');
  }

  async getSkillCategoryById(id: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/skill-categories/${id}`);
  }

  async createSkillCategory(categoryData: any): Promise<AxiosResponse<any>> {
    return this.api.post('/skill-categories', categoryData);
  }

  async updateSkillCategory(id: number, categoryData: any): Promise<AxiosResponse<any>> {
    return this.api.put(`/skill-categories/${id}`, categoryData);
  }

  async deleteSkillCategory(id: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/skill-categories/${id}`);
  }

  // System Reports endpoints
  async getSystemStats(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/system-stats');
  }

  async generateReport(reportType: string, params: any): Promise<AxiosResponse<any>> {
    return this.api.post(`/admin/reports/${reportType}`, params);
  }

  async downloadReport(reportType: string, generatedAt: string): Promise<AxiosResponse<any>> {
    return this.api.get(`/admin/reports/${reportType}/download`, {
      params: { generatedAt },
      responseType: 'blob'
    });
  }

  // User skills endpoints
  async getUserSkills(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/user-skills');
  }

  async getAllUsersSkillsForFeed(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/user-skills/feed');
  }

  async getUserSkillsByType(relationType: 'TEACH' | 'LEARN'): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/user-skills/by-type/${relationType}`);
  }

  async getUserSkillsByUserId(userId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/user-skills/user/${userId}`);
  }

  async getUsersBySkillAndType(skillId: number, relationType: 'TEACH' | 'LEARN'): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/user-skills/skill/${skillId}/type/${relationType}`);
  }

  async addUserSkill(skillData: {
    skillId: number;
    relationType: 'TEACH' | 'LEARN';
    proficiencyLevel: number;
    proficiencyDescription?: string;
    hourlyRate?: number;
    totalSessions?: number;
    availability?: string;
  }): Promise<AxiosResponse<any>> {
    return this.api.post('/user-skills', skillData);
  }

  async updateUserSkill(id: number, skillData: {
    skillId: number;
    relationType: 'TEACH' | 'LEARN';
    proficiencyLevel: number;
    proficiencyDescription?: string;
    hourlyRate?: number;
    totalSessions?: number;
    availability?: string;
  }): Promise<AxiosResponse<any>> {
    return this.api.put(`/user-skills/${id}`, skillData);
  }

  async deleteUserSkill(id: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/user-skills/${id}`);
  }

  // Session requests endpoints
  async getSessionRequests(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/session-requests');
  }

  async getPendingSessionRequests(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/session-requests/pending');
  }

  async getSessionRequestById(id: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/session-requests/${id}`);
  }

  async createSessionRequest(requestData: {
    recipientId: number;
    skillId: number;
    proposedDateTime: string;
    durationMinutes: number;
    message: string;
    teacherId: number;
    learnerId: number;
    meetingLink?: string;
  }): Promise<AxiosResponse<any>> {
    return this.api.post('/session-requests', requestData);
  }

  async acceptSessionRequest(id: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/session-requests/${id}/accept`);
  }

  async rejectSessionRequest(id: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/session-requests/${id}/reject`);
  }

  async cancelSessionRequest(id: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/session-requests/${id}/cancel`);
  }

  // Learning session endpoints
  async createLearningSession(sessionData: any): Promise<AxiosResponse<any>> {
    return this.api.post('/sessions', sessionData);
  }

  async updateLearningSession(id: number, sessionData: any): Promise<AxiosResponse<any>> {
    return this.api.put(`/sessions/${id}`, sessionData);
  }

  // Matchmaking endpoints
  async getMatches(): Promise<AxiosResponse<any>> {
    return this.retryRequest(() => this.api.get('/matches'));
  }

  async getMutualMatches(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/matches/mutual');
  }

  async getUserMatches(userId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/matches/user/${userId}`);
  }

  async getUserMutualMatches(userId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/matches/user/${userId}/mutual`);
  }

  // Chat endpoints
  async getChatRooms(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/chat/rooms');
  }

  async getChatMessages(roomId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/chat/rooms/${roomId}/messages`);
  }

  async sendMessage(messageData: any): Promise<AxiosResponse<any>> {
    return this.api.post('/chat/messages', messageData);
  }

  async markMessagesAsRead(roomId: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/chat/rooms/${roomId}/read`);
  }

  async getOrCreateChatRoom(userId: number, skillId?: number): Promise<AxiosResponse<any>> {
    const url = skillId ? `/chat/rooms/${userId}?skillId=${skillId}` : `/chat/rooms/${userId}`;
    return this.api.post(url);
  }

  async uploadChatFile(formData: FormData): Promise<AxiosResponse<any>> {
    return this.api.post('/chat/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async uploadFile(formData: FormData): Promise<AxiosResponse<any>> {
    return this.api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Session Materials endpoints
  async getSessionMaterials(): Promise<AxiosResponse<any>> {
    return this.api.get('/session-materials');
  }

  async getSessionMaterialsBySession(sessionId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/session-materials/session/${sessionId}`);
  }

  async createSessionMaterial(materialData: any): Promise<AxiosResponse<any>> {
    return this.api.post('/session-materials', materialData);
  }

  async deleteSessionMaterial(materialId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/session-materials/${materialId}`);
  }

  // Sessions endpoints
  async getSessions(): Promise<AxiosResponse<any>> {
    return this.api.get('/sessions');
  }

  async getTeachingSessions(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/sessions/teaching');
  }

  async getLearningSessions(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/sessions/learning');
  }

  async updateSessionMeetingDetails(
    sessionId: number, 
    meetingLink?: string, 
    meetingNotes?: string
  ): Promise<AxiosResponse<any>> {
    const params = new URLSearchParams();
    if (meetingLink !== undefined) params.append('meetingLink', meetingLink);
    if (meetingNotes !== undefined) params.append('meetingNotes', meetingNotes);
    
    return this.api.put(`/sessions/${sessionId}/meeting-details?${params.toString()}`);
  }

  async updateSessionStatus(sessionId: number, status: string): Promise<AxiosResponse<any>> {
    return this.api.put(`/sessions/${sessionId}/status?status=${status}`);
  }

  // Feedback endpoints
  async getFeedback(): Promise<AxiosResponse<any>> {
    return this.api.get('/feedback');
  }

  async getFeedbackGiven(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/feedback/given');
  }

  async getFeedbackById(feedbackId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/feedback/${feedbackId}`);
  }

  async getFeedbackForSession(sessionId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/feedback/session/${sessionId}`);
  }

  async createFeedback(feedbackData: any): Promise<AxiosResponse<any>> {
    return this.api.post('/feedback', feedbackData);
  }

  async updateFeedback(feedbackId: number, feedbackData: any): Promise<AxiosResponse<any>> {
    return this.api.put(`/feedback/${feedbackId}`, feedbackData);
  }

  // Admin Feedback Management
  async getAdminAllFeedback(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/feedback/admin/all');
  }

  async getAdminUserFeedback(userId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/feedback/admin/user/${userId}`);
  }

  // Regular feedback operations
  async deleteFeedback(feedbackId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/feedback/${feedbackId}`);
  }

  // Payment endpoints
  async topUpCredits(amount: number): Promise<AxiosResponse<any>> {
    return this.api.post('/payments/top-up', { amount });
  }

  async getPaymentHistory(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/payments/history');
  }



  // Admin endpoints
  async getAdminDashboard(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/dashboard');
  }

  async getAdminUsers(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/admin/users');
  }

  async blockUser(userId: number, blocked: boolean): Promise<AxiosResponse<any>> {
    return this.api.put(`/admin/users/${userId}/block`, { blocked });
  }

  async adjustUserCredits(userId: number, amount: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/admin/users/${userId}/credits`, { amount });
  }

  async deleteUser(userId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/admin/users/${userId}`);
  }

  // Notes endpoints
  async getNotes(search?: string): Promise<AxiosResponse<any[]>> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get(`/notes${params}`);
  }

  async getNoteById(noteId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/notes/${noteId}`);
  }

  async createNote(noteData: { heading: string; body: string }): Promise<AxiosResponse<any>> {
    return this.api.post('/notes', noteData);
  }

  async updateNote(noteId: number, noteData: { heading: string; body: string }): Promise<AxiosResponse<any>> {
    return this.api.put(`/notes/${noteId}`, noteData);
  }

  async deleteNote(noteId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/notes/${noteId}`);
  }

  // Saved Feeds endpoints
  async saveFeed(feedData: { userSkillId: number; notes?: string }): Promise<AxiosResponse<any>> {
    return this.api.post('/saved-feeds', feedData);
  }

  async getSavedFeeds(search?: string): Promise<AxiosResponse<any[]>> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get(`/saved-feeds${params}`);
  }

  async removeSavedFeed(savedFeedId: number): Promise<AxiosResponse<any>> {
    return this.api.delete(`/saved-feeds/${savedFeedId}`);
  }

  async updateSavedFeedNotes(savedFeedId: number, notes: string): Promise<AxiosResponse<any>> {
    return this.api.put(`/saved-feeds/${savedFeedId}/notes`, notes);
  }

  // Admin Session Management
  async getAdminAllSessions(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/sessions/admin/all');
  }

  async getAdminSessionsByStatus(status: string): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/sessions/admin/status/${status}`);
  }

  async getAdminUserSessions(userId: number): Promise<AxiosResponse<any[]>> {
    return this.api.get(`/sessions/admin/user/${userId}`);
  }

  async getAdminSessionStats(): Promise<AxiosResponse<any>> {
    return this.api.get('/sessions/admin/stats');
  }

  async processSessionCredits(sessionId: number): Promise<AxiosResponse<any>> {
    return this.api.put(`/admin/sessions/${sessionId}/process-credits`);
  }

  // Admin Session Request Management
  async getAdminAllSessionRequests(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/session-requests/admin/all');
  }

  // Admin Matchmaking
  async getAdminUserMatches(userId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/matches/user/${userId}`);
  }

  async getAdminUserMutualMatches(userId: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/matches/user/${userId}/mutual`);
  }

  async getAdminMatchmakingOverview(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/matchmaking/overview');
  }

  // Admin Analytics & Reports
  async getAdminUserActivityReport(startDate: string, endDate: string): Promise<AxiosResponse<any>> {
    return this.api.get(`/admin/reports/user-activity?startDate=${startDate}&endDate=${endDate}`);
  }

  async getAdminPopularSkillsReport(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/reports/popular-skills');
  }

  async getAdminUserEngagementReport(): Promise<AxiosResponse<any>> {
    return this.api.get('/admin/reports/user-engagement');
  }



  async getSessionStats(): Promise<AxiosResponse<any>> {
    return this.api.get('/sessions/admin/stats');
  }

  async getCreditTransactions(): Promise<AxiosResponse<any[]>> {
    return this.retryRequest(() => this.api.get('/users/credit-transactions'));
  }

  async getAllSessionRequests(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/session-requests/admin/all');
  }

  async createPaymentIntent(data: { creditAmount: number; token: string }): Promise<AxiosResponse<any>> {
    return this.api.post('/payments/create-payment-intent', data);
  }

  async confirmPayment(paymentIntentId: string): Promise<AxiosResponse<any>> {
    return this.api.post(`/payments/confirm-payment/${paymentIntentId}`);
  }

  // Chatbot endpoints
  async chatbotChat(message: { message: string }): Promise<AxiosResponse<any>> {
    return this.api.post('/chatbot/chat', message);
  }
}

export const apiService = new ApiService();
export default apiService; 